import React, { useState, useEffect, useRef, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { usePageTitle } from '../hooks/usePageTitle'
import useLang from '../hooks/useLang'
import '../pages/Home.css'
import { Search, MapPin, Briefcase, Clock, ArrowUpRight, X, Banknote } from 'lucide-react'
import { supabase } from '../supabaseClient'
import { translateText } from '../utils/translate'

import logoQaraa from '../images/logo-qaraa.webp'
import qaraaCrmLogo from '../images/qaraaxqaraa-crm.webp'
import sadness from '../images/sadness.webp'

function VacancyCard({ v, onClick }) {
  const [hovered, setHovered] = useState(false)
  return (
    <article
      onClick={() => onClick(v)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: '#F8F9FA', borderRadius: '20px', padding: '28px',
        display: 'flex', flexDirection: 'column', cursor: 'pointer',
        border: '1px solid #E5E5EA',
        transition: 'transform 0.25s ease, box-shadow 0.25s ease',
        transform: hovered ? 'translateY(-4px)' : 'translateY(0)',
        boxShadow: hovered ? '0 8px 24px rgba(0,0,0,0.08)' : '0 2px 8px rgba(0,0,0,0.04)'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', marginBottom: '6px' }}>
        <h2 style={{ fontSize: '19px', fontWeight: '600', color: '#1C1C1E', margin: 0, lineHeight: '1.3', letterSpacing: '-0.01em' }}>{v.title}</h2>
        <div style={{
          flexShrink: 0, width: '32px', height: '32px', borderRadius: '50%',
          background: hovered ? '#007AFF' : '#FFFFFF',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'background 0.2s ease'
        }}>
          <ArrowUpRight size={16} color={hovered ? '#FFF' : '#1C1C1E'} />
        </div>
      </div>
      <p style={{ fontSize: '13px', fontWeight: '500', color: '#007AFF', margin: '0 0 14px' }}>{v.department}</p>
      <p style={{ fontSize: '15px', color: '#636366', lineHeight: '1.5', margin: '0 0 20px', flexGrow: 1 }}>{v.description}</p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px', paddingTop: '16px', borderTop: '1px solid #E5E5EA' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#636366' }}><MapPin size={14} />{v.location}</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#636366' }}><Briefcase size={14} />{v.type}</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#636366' }}><Clock size={14} />{v.format}</span>
      </div>
      <p style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '15px', fontWeight: '600', color: '#1C1C1E', margin: '14px 0 0' }}>
        <Banknote size={16} color="#34C759" /> {v.salary} ₸
      </p>
    </article>
  )
}

export default function Vacancies() {
  const { lang, changeLang, t, languages } = useLang()
  usePageTitle(t.footerVacancies)
  const navigate = useNavigate()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [langDropdownOpen, setLangDropdownOpen] = useState(false)
  const langRef = useRef(null)
  
  const allLabel = lang === 'en' ? 'All' : lang === 'kk' ? 'Барлығы' : 'Все'
  
  const [query, setQuery] = useState('')
  const [selectedDept, setSelectedDept] = useState(allLabel)

  // Data
  const [vacancies, setVacancies] = useState(() => {
    const cached = localStorage.getItem('vacancies_cache')
    return cached ? JSON.parse(cached) : []
  })
  const [translatedVacancies, setTranslatedVacancies] = useState(() => {
    return lang === 'ru' ? (localStorage.getItem('vacancies_cache') ? JSON.parse(localStorage.getItem('vacancies_cache')) : []) : []
  })
  const [loading, setLoading] = useState(vacancies.length === 0)

  // Modal vacancy detail
  const [selectedVacancy, setSelectedVacancy] = useState(null)
  const [applyMode, setApplyMode] = useState(false)

  // Application form
  const [appForm, setAppForm] = useState({ user_name: '', phone: '', message: '' })
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const currentUser = useMemo(() => {
    try {
      const raw = localStorage.getItem('qaraa_user')
      if (raw) return JSON.parse(raw)
    } catch {}
    return null
  }, [])

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) setLangDropdownOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => { window.scrollTo(0, 0) }, [])

  useEffect(() => {
    let isMounted = true;
    const fetchVacancies = async () => {
      const { data, error } = await supabase
        .from('vacancies')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false })
      if (!error && data && isMounted) {
        setVacancies(data)
        localStorage.setItem('vacancies_cache', JSON.stringify(data))
        setLoading(false)
      }
    }

    fetchVacancies()

    const interval = setInterval(() => {
      fetchVacancies()
    }, 5000)

    const subscription = supabase
      .channel('public:vacancies')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'vacancies' }, () => {
        fetchVacancies()
      })
      .subscribe()

    return () => {
      isMounted = false;
      clearInterval(interval)
      supabase.removeChannel(subscription)
    }
  }, [])

  useEffect(() => {
    let isMounted = true;
    const doTranslation = async () => {
      if (lang === 'ru') {
        if (isMounted) setTranslatedVacancies(vacancies);
        return;
      }
      
      const newVacs = await Promise.all(vacancies.map(async (v) => {
        const title = await translateText(v.title, lang);
        const description = await translateText(v.description, lang);
        const department = await translateText(v.department, lang);
        const type = await translateText(v.type, lang);
        const format = await translateText(v.format, lang);
        const location = await translateText(v.location, lang);
        return { ...v, title, description, department, type, format, location };
      }));
      
      if (isMounted) setTranslatedVacancies(newVacs);
    };
    doTranslation();
    return () => { isMounted = false; };
  }, [vacancies, lang]);

  const availableDepts = useMemo(() => {
    const depts = new Set(translatedVacancies.map(v => v.department))
    return [allLabel, ...Array.from(depts)]
  }, [translatedVacancies, allLabel])

  const filtered = useMemo(() => {
    return translatedVacancies.filter((v) => {
      const q = query.trim().toLowerCase()
      const matchQuery = q.length === 0 || v.title.toLowerCase().includes(q) || v.description.toLowerCase().includes(q)
      const matchDept = selectedDept === allLabel || v.department === selectedDept
      return matchQuery && matchDept
    })
  }, [query, selectedDept, translatedVacancies, allLabel])

  // Если выбранного отдела больше нет в списке, сбрасываем на "Все"
  useEffect(() => {
    if (selectedDept !== allLabel && !availableDepts.includes(selectedDept)) {
      setSelectedDept(allLabel)
    }
  }, [availableDepts, selectedDept, allLabel])

  const openVacancy = (v) => {
    setSelectedVacancy(v)
    setApplyMode(false)
    setSubmitted(false)
    setAppForm({ user_name: '', phone: '', message: '' })
  }

  const closeModal = () => {
    setSelectedVacancy(null)
    setApplyMode(false)
    setSubmitted(false)
  }

  const handleApplyClick = () => {
    if (!currentUser) {
      navigate('/login')
      return
    }
    setApplyMode(true)
    setAppForm({ user_name: currentUser.name || '', phone: currentUser.phone || '', message: '' })
  }

  const handleSubmitApplication = async () => {
    if (!appForm.user_name || !appForm.phone) return
    setSubmitting(true)
    const { error } = await supabase.from('vacancy_applications').insert([{
      vacancy_id: selectedVacancy.id,
      user_phone: currentUser?.phone || null,
      user_name: appForm.user_name,
      phone: appForm.phone,
      message: appForm.message,
      status: 'new'
    }])
    setSubmitting(false)
    if (!error) {
      setSubmitted(true)
      setApplyMode(false)
    }
  }

  const font = '-apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif'
  const inputStyle = { width: '100%', boxSizing: 'border-box', padding: '11px 14px', border: '1.5px solid #E5E5EA', borderRadius: '10px', fontSize: '15px', fontFamily: font, outline: 'none', color: '#1C1C1E', background: '#fff' }

  const emptyStateText = lang === 'en' 
    ? "Sigh… there are no vacancies yet. But maybe they'll appear soon." 
    : lang === 'kz' 
      ? "Эхх… әзірге бос орындар жоқ. Бірақ жақында пайда болып қалар." 
      : "Эхх… вакансий пока нет. Но, возможно, скоро появятся.";
      
  const searchPlaceholder = lang === 'en' ? "Search for jobs" : lang === 'kz' ? "Бос орындар іздеу" : "Поиск по вакансиям";

  const tStrings = {
    applyBtn: lang === 'en' ? "Apply" : lang === 'kz' ? "Өтініш қалдыру" : "Откликнуться",
    authReq: lang === 'en' ? "Authorization required" : lang === 'kz' ? "Авторизация қажет" : "Нужна авторизация",
    openPos: (n) => {
      if (lang === 'en') return n === 1 ? 'open position' : 'open positions';
      if (lang === 'kz') return 'ашық жұмыс орны';
      return n === 1 ? 'открытая позиция' : 'открытых позиций';
    },
    leaveApp: lang === 'en' ? "Submit application" : lang === 'kz' ? "Өтініш қалдыру" : "Оставить заявку",
    nameLabel: lang === 'en' ? "Your name" : lang === 'kz' ? "Аты-жөніңіз" : "Ваше имя",
    namePlace: lang === 'en' ? "First and Last name" : lang === 'kz' ? "Аты жөні" : "Имя Фамилия",
    phoneLabel: lang === 'en' ? "Phone" : lang === 'kz' ? "Телефон" : "Телефон",
    letterLabel: lang === 'en' ? "Cover letter (optional)" : lang === 'kz' ? "Ілеспе хат (міндетті емес)" : "Сопроводительное письмо (необязательно)",
    letterPlace: lang === 'en' ? "Tell us about yourself and why you want to work at qaraa..." : lang === 'kz' ? "Өзіңіз туралы және неліктен qaraa-да жұмыс істегіңіз келетінін айтып беріңіз..." : "Расскажите о себе и почему хотите работать в qaraa...",
    backBtn: lang === 'en' ? "Back" : lang === 'kz' ? "Артқа" : "Назад",
    sendBtn: lang === 'en' ? "Send application" : lang === 'kz' ? "Өтінішті жіберу" : "Отправить заявку",
    sending: lang === 'en' ? "Sending..." : lang === 'kz' ? "Жіберілуде..." : "Отправка...",
    successTitle: lang === 'en' ? "Application sent!" : lang === 'kz' ? "Өтініш жіберілді!" : "Заявка отправлена!",
    successSub: lang === 'en' ? "We will contact you shortly" : lang === 'kz' ? "Біз сізбен жақын арада хабарласамыз" : "Мы свяжемся с вами в ближайшее время"
  };

  const introText = {
    ru: [
      "Кажется, мы снова решили, что нам не хватает людей.",
      "Поэтому ищем новых членов команды.",
      "Кого именно - не знаем. Продавцов, дизайнеров, разработчиков, менеджеров, адекватных людей… в общем, смотрите вакансии ниже.",
      "Если подходящей позиции нет — не расстраивайся. Возможно, мы просто ещё не придумали её."
    ],
    en: [
      "You came here looking for a job.",
      "We respect the desperation.",
      "Below are the open positions at qaraa. Take a look around, find something you like, and send us your application.",
      "Worst case? You’re back here tomorrow."
    ],
    kz: [
      "Ақша керек.",
      "Ақша үшін жұмыс керек.",
      "Жұмыс үшін тәжірибе керек.",
      "Тәжірибе үшін жұмыс керек.",
      "Қысқасы, төмендегі вакансияларды қарап шыққан дұрыс."
    ]
  };

  const currentIntro = introText[lang] || introText['ru'];

  return (
    <div style={{ minHeight: '100vh', background: '#FFFFFF' }}>
      <header style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        background: 'rgba(255,255,255,0.72)',
        backdropFilter: 'saturate(180%) blur(20px)',
        WebkitBackdropFilter: 'saturate(180%) blur(20px)',
        transition: 'all 0.3s ease'
      }}>
        <div className="home-header-container" style={{ maxWidth: '1440px', margin: '0 auto', padding: '20px 48px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div className="home-logo-container" style={{ display: 'flex', alignItems: 'center', gap: '14px', cursor: 'pointer', transition: 'opacity 0.3s ease' }}
            onClick={() => navigate('/')}
            onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
            onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
          >
            <img src={logoQaraa} alt="qaraa" className="home-logo" style={{ height: '42px', width: 'auto' }} />
            <div>
              <div className="home-logo-title" style={{ fontSize: '17px', fontWeight: '600', color: '#1C1C1E', lineHeight: '1.2' }}>
                <span className="logo-title-line1">qaraa.asia</span>
                <br className="mobile-break" />
                <span className="logo-title-line2 mobile-only-subtitle" style={{ fontSize: '12px', color: '#8E8E93', fontWeight: '500' }}>{t.logoSubtitle}</span>
              </div>
              <div className="home-logo-subtitle desktop-only-subtitle" style={{ fontSize: '12px', color: '#8E8E93', marginTop: '4px' }}>{t.logoSubtitle}</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <div ref={langRef} style={{ position: 'relative', marginRight: '6px' }}>
              <button onClick={() => setLangDropdownOpen(!langDropdownOpen)} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', background: langDropdownOpen ? 'rgba(0,0,0,0.06)' : 'transparent', border: 'none', borderRadius: '100px', fontSize: '14px', fontWeight: '500', color: '#8E8E93', cursor: 'pointer', transition: 'all 0.2s ease', fontFamily: 'inherit' }}
                onMouseEnter={(e) => { if (!langDropdownOpen) e.currentTarget.style.background = 'rgba(0,0,0,0.04)' }}
                onMouseLeave={(e) => { if (!langDropdownOpen) e.currentTarget.style.background = 'transparent' }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#8E8E93" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
                <span className="lang-text-label">{t.languageLabel}</span>
                <svg className="lang-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#8E8E93" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transform: langDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}><polyline points="6 9 12 15 18 9"/></svg>
              </button>
              {langDropdownOpen && (
                <div style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, background: '#FFFFFF', borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.12)', padding: '6px', minWidth: '140px', zIndex: 200 }}>
                  {languages.map((l) => (
                    <button key={l.code} onClick={() => { changeLang(l.code); setLangDropdownOpen(false) }} style={{ display: 'block', width: '100%', padding: '10px 14px', background: lang === l.code ? 'rgba(0,122,255,0.08)' : 'transparent', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: lang === l.code ? '600' : '400', color: lang === l.code ? '#007AFF' : '#1C1C1E', cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s ease', fontFamily: 'inherit' }}
                      onMouseEnter={(e) => { if (lang !== l.code) e.currentTarget.style.background = 'rgba(0,0,0,0.04)' }}
                      onMouseLeave={(e) => { if (lang !== l.code) e.currentTarget.style.background = 'transparent' }}
                    >{l.label}</button>
                  ))}
                </div>
              )}
            </div>
            <button onClick={() => navigate('/login')} className="home-header-button-login" style={{ padding: '10px 22px', background: 'transparent', border: 'none', borderRadius: '100px', fontSize: '15px', fontWeight: '600', color: '#000000', cursor: 'pointer', transition: 'all 0.25s ease', letterSpacing: '-0.3px', fontFamily: 'inherit' }}
              onMouseEnter={(e) => { e.target.style.background = 'rgba(0,0,0,0.06)'; e.target.style.transform = 'scale(1.02)' }}
              onMouseLeave={(e) => { e.target.style.background = 'transparent'; e.target.style.transform = 'scale(1)' }}
            >{t.login}</button>
            <button onClick={() => navigate('/login?mode=register')} className="home-header-button-register" style={{ padding: '10px 22px', background: '#FFFFFF', border: '1.5px solid #007AFF', borderRadius: '100px', fontSize: '15px', fontWeight: '600', color: '#007AFF', cursor: 'pointer', transition: 'all 0.25s ease', letterSpacing: '-0.3px', fontFamily: 'inherit' }}
              onMouseEnter={(e) => { e.target.style.background = '#007AFF'; e.target.style.color = '#FFFFFF'; e.target.style.transform = 'scale(1.02)' }}
              onMouseLeave={(e) => { e.target.style.background = '#FFFFFF'; e.target.style.color = '#007AFF'; e.target.style.transform = 'scale(1)' }}
            >{t.register}</button>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="home-mobile-menu-button" style={{ display: 'none', padding: '8px', background: 'transparent', border: 'none', cursor: 'pointer', flexDirection: 'column', gap: '5px' }}>
              <span style={{ width: '24px', height: '2px', background: '#000', borderRadius: '2px', transition: 'all 0.3s', transform: mobileMenuOpen ? 'rotate(45deg) translateY(7px)' : 'none' }}></span>
              <span style={{ width: '24px', height: '2px', background: '#000', borderRadius: '2px', transition: 'all 0.3s', opacity: mobileMenuOpen ? 0 : 1 }}></span>
              <span style={{ width: '24px', height: '2px', background: '#000', borderRadius: '2px', transition: 'all 0.3s', transform: mobileMenuOpen ? 'rotate(-45deg) translateY(-7px)' : 'none' }}></span>
            </button>
          </div>
        </div>
      </header>

      {mobileMenuOpen && (
        <div className="home-mobile-menu" style={{ position: 'fixed', top: '76px', left: 0, right: 0, background: 'rgba(255,255,255,0.95)', backdropFilter: 'saturate(180%) blur(20px)', WebkitBackdropFilter: 'saturate(180%) blur(20px)', padding: '16px 20px', zIndex: 99, display: 'flex', flexDirection: 'column', gap: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
          <button onClick={() => { navigate('/login'); setMobileMenuOpen(false) }} style={{ padding: '12px 20px', background: 'rgba(0,0,0,0.06)', border: 'none', borderRadius: '12px', fontSize: '15px', fontWeight: '600', color: '#000', cursor: 'pointer', width: '100%', fontFamily: 'inherit' }}>{t.login}</button>
          <button onClick={() => { navigate('/login?mode=register'); setMobileMenuOpen(false) }} style={{ padding: '12px 20px', background: 'linear-gradient(180deg, #007AFF 0%, #0051D5 100%)', border: 'none', borderRadius: '12px', fontSize: '15px', fontWeight: '600', color: '#FFF', cursor: 'pointer', width: '100%', fontFamily: 'inherit' }}>{t.register}</button>
        </div>
      )}

      {/* Content */}
      <div className="vacancies-content-wrapper">
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{ fontSize: '48px', fontWeight: '600', letterSpacing: '-0.02em', color: '#1C1C1E', margin: '0 0 16px', lineHeight: '1.15' }} className="vacancies-title">{t.footerVacancies}</h1>
          <div style={{ fontSize: '17px', color: '#636366', margin: 0, lineHeight: '1.5', maxWidth: '600px' }}>
            {currentIntro.map((line, i) => (
              <p key={i} style={{ margin: 0 }}>{line}</p>
            ))}
          </div>
        </div>

        {availableDepts.length > 1 && (
          <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '12px', marginBottom: '12px', msOverflowStyle: 'none', scrollbarWidth: 'none' }} className="vacancies-dept-filters">
            {availableDepts.map(d => (
              <button key={d} onClick={() => setSelectedDept(d)} style={{
                padding: '8px 18px', borderRadius: '100px', whiteSpace: 'nowrap',
                background: selectedDept === d ? '#1C1C1E' : '#F2F2F7',
                color: selectedDept === d ? '#FFF' : '#1C1C1E',
                border: 'none', fontSize: '14px', fontWeight: '600',
                cursor: 'pointer', transition: 'all 0.2s ease', fontFamily: 'inherit'
              }}>
                {d}
              </button>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '32px' }}>
          <div style={{ position: 'relative', width: '100%', maxWidth: '340px' }} className="vacancies-search">
            <Search size={16} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#A1A1A6', pointerEvents: 'none' }} />
            <input type="text" placeholder={searchPlaceholder} value={query} onChange={(e) => setQuery(e.target.value)} style={{ width: '100%', boxSizing: 'border-box', background: '#F2F2F7', border: '1px solid transparent', borderRadius: '14px', padding: '12px 14px 12px 42px', fontSize: '15px', color: '#1C1C1E', fontFamily: 'inherit', outline: 'none', transition: 'border-color 0.2s, background 0.2s' }}
              onFocus={(e) => { e.target.style.background = '#FFF'; e.target.style.borderColor = '#007AFF' }}
              onBlur={(e) => { e.target.style.background = '#F2F2F7'; e.target.style.borderColor = 'transparent' }}
            />
          </div>
        </div>

        <p style={{ fontSize: '14px', color: '#636366', margin: '0 0 24px' }}>{filtered.length} {tStrings.openPos(filtered.length)}</p>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '80px', color: '#8E8E93', fontSize: '16px' }}>Загрузка вакансий...</div>
        ) : filtered.length > 0 ? (
          <div className="vacancies-grid">
            {filtered.map((v) => <VacancyCard key={v.id} v={v} onClick={openVacancy} />)}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#636366', background: '#F8F9FA', borderRadius: '24px' }}>
            <img src={sadness} alt="sadness" style={{ width: '120px', marginBottom: '16px', borderRadius: '12px' }} />
            <p style={{ fontSize: '16px', margin: 0, fontWeight: '500', color: '#1C1C1E' }}>{emptyStateText}</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer style={{ padding: '80px 24px 50px', background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)', color: '#1C1C1E', position: 'relative', borderRadius: '60px 60px 0 0', marginTop: '60px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '50px', marginBottom: '50px' }}>
            <div>
              <div style={{ fontSize: '24px', fontWeight: '800', marginBottom: '16px', color: '#1C1C1E', letterSpacing: '-0.5px' }}>qaraa.asia</div>
              <div style={{ marginBottom: '16px' }}><img src={qaraaCrmLogo} alt="qaraa x qaraa.crm" style={{ height: '100px', width: 'auto' }} /></div>
              <div style={{ fontSize: '15px', color: '#636366', fontWeight: '500' }}>{t.logoSubtitle}</div>
            </div>
            <div>
              <div style={{ fontSize: '13px', fontWeight: '800', color: '#8E8E93', marginBottom: '20px', letterSpacing: '1.2px', textTransform: 'uppercase' }}>{t.footerNavigation}</div>
              {[{ name: t.footerAbout, path: '/about-us' }, { name: t.footerBonusCard, path: '/card-info' }, { name: t.footerVacancies, path: '/vacancies' }, { name: t.footerBusiness, path: '/business' }].map(l => (
                <div key={l.path} onClick={() => navigate(l.path)} style={{ fontSize: '15px', marginBottom: '12px', cursor: 'pointer', color: '#1C1C1E', fontWeight: '500', transition: 'all 0.2s ease' }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = '#5856D6'; e.currentTarget.style.paddingLeft = '8px' }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = '#1C1C1E'; e.currentTarget.style.paddingLeft = '0' }}>{l.name}</div>
              ))}
            </div>
            <div>
              <div style={{ fontSize: '13px', fontWeight: '800', color: '#8E8E93', marginBottom: '20px', letterSpacing: '1.2px', textTransform: 'uppercase' }}>{t.footerSocial}</div>
              {[{ name: 'Instagram', url: 'https://www.instagram.com/qaraa.asia?igsh=cWw3cmlsNmJ0ZHRi' }, { name: 'TikTok', url: 'https://www.tiktok.com/@qaraa.asia' }, { name: 'Telegram', url: 'https://t.me/qaraa_kz' }, { name: 'WhatsApp', url: 'https://wa.me/77778307588' }].map(s => (
                <a key={s.url} href={s.url} target="_blank" rel="noopener noreferrer" style={{ display: 'block', fontSize: '15px', color: '#1C1C1E', fontWeight: '500', textDecoration: 'none', marginBottom: '12px', transition: 'all 0.2s ease' }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = '#5856D6'; e.currentTarget.style.paddingLeft = '8px' }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = '#1C1C1E'; e.currentTarget.style.paddingLeft = '0' }}>{s.name}</a>
              ))}
            </div>
            <div>
              <div style={{ fontSize: '13px', fontWeight: '800', color: '#8E8E93', marginBottom: '20px', letterSpacing: '1.2px', textTransform: 'uppercase' }}>{t.footerInfo}</div>
              <div style={{ fontSize: '15px', marginBottom: '12px', color: '#1C1C1E', fontWeight: '500' }}>+7 777 830 75 88</div>
              <div style={{ fontSize: '15px', color: '#636366', fontWeight: '500' }}>{t.footerCity}</div>
              <div style={{ fontSize: '15px', marginTop: '12px', color: '#636366', fontWeight: '500' }}>{t.footerAddress}</div>
            </div>
          </div>
          <div style={{ marginTop: '80px', paddingTop: '40px', borderTop: '2px solid rgba(0,0,0,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '20px', fontSize: '15px', color: '#8E8E93', fontWeight: '600' }}>
            <div>{t.footerRights}</div>
            <div style={{ display: 'flex', gap: '24px' }}>
              <span onClick={() => navigate('/privacy')} style={{ cursor: 'pointer', transition: 'all 0.2s ease' }} onMouseEnter={(e) => { e.currentTarget.style.color = '#5856D6' }} onMouseLeave={(e) => { e.currentTarget.style.color = '#8E8E93' }}>{t.footerPrivacy}</span>
              <span onClick={() => navigate('/terms')} style={{ cursor: 'pointer', transition: 'all 0.2s ease' }} onMouseEnter={(e) => { e.currentTarget.style.color = '#5856D6' }} onMouseLeave={(e) => { e.currentTarget.style.color = '#8E8E93' }}>{t.footerTerms}</span>
            </div>
          </div>
        </div>
      </footer>

      {/* VACANCY DETAIL MODAL */}
      {selectedVacancy && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', backdropFilter: 'blur(4px)' }}
          onClick={e => { if (e.target === e.currentTarget) closeModal() }}>
          <div style={{ background: '#fff', borderRadius: '24px', width: '100%', maxWidth: '580px', maxHeight: '90vh', overflow: 'auto', padding: '36px', boxShadow: '0 24px 80px rgba(0,0,0,0.2)', fontFamily: font }}>

            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
              <div>
                <div style={{ fontSize: '12px', fontWeight: '600', color: '#007AFF', marginBottom: '6px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>{selectedVacancy.department}</div>
                <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#1C1C1E', margin: 0, letterSpacing: '-0.5px', lineHeight: '1.2' }}>{selectedVacancy.title}</h2>
              </div>
              <button onClick={closeModal} style={{ background: '#F2F2F7', border: 'none', borderRadius: '50%', width: '36px', height: '36px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><X size={18} color="#636366" /></button>
            </div>

            {/* Tags */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '24px' }}>
              {[{ icon: <MapPin size={13} />, text: selectedVacancy.location }, { icon: <Briefcase size={13} />, text: selectedVacancy.type }, { icon: <Clock size={13} />, text: selectedVacancy.format }].map((tag, i) => (
                <span key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#636366', background: '#F2F2F7', padding: '6px 12px', borderRadius: '980px' }}>{tag.icon}{tag.text}</span>
              ))}
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', fontWeight: '600', color: '#1C1C1E', background: '#F0FFF4', padding: '6px 14px', borderRadius: '980px', border: '1px solid #34C759' }}>
                <Banknote size={14} color="#34C759" /> {selectedVacancy.salary} ₸
              </span>
            </div>

            {/* Description */}
            <div style={{ fontSize: '15px', color: '#636366', lineHeight: '1.7', marginBottom: '32px', whiteSpace: 'pre-wrap' }}>{selectedVacancy.description}</div>

            {/* Apply form or submitted */}
            {submitted ? (
              <div style={{ background: '#F0FFF4', border: '1px solid #34C759', borderRadius: '16px', padding: '24px', textAlign: 'center' }}>
                <div style={{ fontSize: '32px', marginBottom: '8px' }}>✅</div>
                <div style={{ fontSize: '17px', fontWeight: '600', color: '#1C1C1E' }}>{tStrings.successTitle}</div>
                <div style={{ fontSize: '14px', color: '#636366', marginTop: '4px' }}>{tStrings.successSub}</div>
              </div>
            ) : applyMode ? (
              <div style={{ animation: 'fadeIn 0.3s ease' }}>
                <div style={{ fontSize: '17px', fontWeight: '600', color: '#1C1C1E', marginBottom: '16px' }}>{tStrings.leaveApp}</div>
                {[{ label: tStrings.nameLabel, key: 'user_name', placeholder: tStrings.namePlace }, { label: tStrings.phoneLabel, key: 'phone', placeholder: '+7 777 000 00 00' }].map(({ label, key, placeholder }) => (
                  <div key={key} style={{ marginBottom: '14px' }}>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#636366', marginBottom: '6px' }}>{label}</div>
                    <input value={appForm[key]} onChange={e => setAppForm(f => ({ ...f, [key]: e.target.value }))} placeholder={placeholder} style={inputStyle}
                      onFocus={e => e.target.style.borderColor = '#007AFF'} onBlur={e => e.target.style.borderColor = '#E5E5EA'} />
                  </div>
                ))}
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: '#636366', marginBottom: '6px' }}>{tStrings.letterLabel}</div>
                  <textarea value={appForm.message} onChange={e => setAppForm(f => ({ ...f, message: e.target.value }))} placeholder={tStrings.letterPlace} rows={4}
                    style={{ ...inputStyle, resize: 'vertical' }}
                    onFocus={e => e.target.style.borderColor = '#007AFF'} onBlur={e => e.target.style.borderColor = '#E5E5EA'} />
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button onClick={() => setApplyMode(false)} style={{ flex: 1, padding: '13px', background: '#F2F2F7', border: 'none', borderRadius: '12px', fontSize: '15px', fontWeight: '600', color: '#636366', cursor: 'pointer', fontFamily: font }}>{tStrings.backBtn}</button>
                  <button onClick={handleSubmitApplication} disabled={submitting || !appForm.user_name || !appForm.phone} style={{ flex: 2, padding: '13px', background: submitting ? '#8E8E93' : '#007AFF', border: 'none', borderRadius: '12px', fontSize: '15px', fontWeight: '600', color: '#fff', cursor: submitting ? 'not-allowed' : 'pointer', fontFamily: font, transition: 'opacity 0.2s' }}>{submitting ? tStrings.sending : tStrings.sendBtn}</button>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: '12px' }}>
                <button onClick={handleApplyClick} style={{ flex: 1, padding: '14px', background: '#FFFFFF', border: '1.5px solid #007AFF', borderRadius: '100px', fontSize: '16px', fontWeight: '600', color: '#007AFF', cursor: 'pointer', fontFamily: font, transition: 'all 0.25s ease', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#007AFF'; e.currentTarget.style.color = '#FFFFFF'; }} 
                  onMouseLeave={e => { e.currentTarget.style.background = '#FFFFFF'; e.currentTarget.style.color = '#007AFF'; }}>
                  {tStrings.applyBtn} <ArrowUpRight size={18} />
                </button>
                {!currentUser && (
                  <div style={{ fontSize: '13px', color: '#8E8E93', display: 'flex', alignItems: 'center' }}>{tStrings.authReq}</div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <style>{`
        .vacancies-content-wrapper {
          max-width: 1080px; 
          margin: 0 auto; 
          padding: 80px 24px 96px; 
          font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif;
        }
        .vacancies-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
          gap: 20px;
        }
        .vacancies-dept-filters::-webkit-scrollbar {
          display: none;
        }
        .mobile-break { display: none; }
        @media (max-width: 768px) {
          .vacancies-content-wrapper { padding: 76px 16px 60px; }
          .vacancies-title { font-size: 34px !important; }
          .vacancies-grid { grid-template-columns: 1fr; gap: 16px; }
          .vacancies-search { max-width: 100% !important; }
          .mobile-break { display: inline; }
          .home-mobile-menu-button { display: flex !important; flex-direction: column !important; gap: 5px !important; }
          .home-header-button-login, .home-header-button-register { display: none !important; }
          .lang-text-label { display: none !important; }
          .lang-chevron { display: none !important; }
          .home-logo-subtitle.desktop-only-subtitle { display: none !important; }
          .mobile-only-subtitle { display: none !important; }
          .home-header-container { padding: 14px 16px !important; }
          .home-logo-container { gap: 10px !important; }
        }
      `}</style>
    </div>
  )
}
